import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Avatar, Box, Breadcrumbs, Button, Divider, IconButton, Menu, MenuItem, Stack, Tab, Tabs, Typography } from '@mui/material';
import imageService from '../../services/imageService';
import { Store as StoreType, StoreUser } from '../../services/authService';
import { toast } from 'react-toastify';
import { formatDateMMDDYYYY, formatPhoneNumber } from '../../utils/formatUtils';
import { formatTimeOfDay } from '../../utils/formatters/timeFormatters';
import AdminStatusPill, { type AdminStatusPillTone } from '../admin/AdminStatusPill';
import AdminSurface from '../admin/AdminSurface';
import { adminColors, adminRadii, adminTypography } from '../../styles/adminDesignTokens';
import {
  adminCombineSx,
  adminDividerSx,
  adminIconActionButtonSx,
  adminLinkButtonSx,
  adminTabsSx,
} from '../../styles/adminComponentStyles';

type StoreDetailsHeaderProps = {
  store: StoreType | null;
  storeOwner?: StoreUser | null;
  onEdit?: () => void;
  onBack?: () => void;
  showEdit?: boolean;
  backLabel?: string;
  tabs?: Array<{ key: string; label: string }>;
  activeTab?: string;
  onTabChange?: (tabKey: string) => void;
  globalActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  enableStoreSwitcher?: boolean;
  storesForSwitcher?: StoreType[];
  activeStoreId?: string;
  onStoreChange?: (storeId: string) => void;
};

const StoreDetailsHeader: React.FC<StoreDetailsHeaderProps> = ({
  store,
  storeOwner = null,
  onEdit,
  onBack,
  showEdit = true,
  backLabel = 'Stores',
  tabs = [],
  activeTab,
  onTabChange,
  globalActions,
  rightActions,
  enableStoreSwitcher = false,
  storesForSwitcher = [],
  activeStoreId,
  onStoreChange,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string>('/default_store_logo.svg');
  const [storeMenuAnchor, setStoreMenuAnchor] = useState<HTMLElement | null>(null);
  const storeClosingTime = formatTimeOfDay(store?.store_closing_time);
  const storeSwitcherOptions = useMemo(() => {
    const byId = new Map<string, StoreType>();
    if (store?.id) {
      byId.set(store.id, store);
    }
    storesForSwitcher.forEach((candidate) => {
      if (candidate?.id) {
        byId.set(candidate.id, candidate);
      }
    });
    return Array.from(byId.values());
  }, [store, storesForSwitcher]);

  const loadCurrentImage = useCallback(async () => {
    if (!store) return;
    try {
      if (store.logo_image_url && store.logo_image_url !== null && store.logo_image_url.trim() !== '') {
        const blobUrl = await imageService.getStoreImage(store.id);
        setPreviewUrl(blobUrl);
      } else {
        setPreviewUrl('/default_store_logo.svg');
      }
    } catch (error) {
      setPreviewUrl('/default_store_logo.svg');
    }
  }, [store]);

  useEffect(() => {
    loadCurrentImage();
    return () => {
      // no-op: imageService manages blob URL lifecycle
    };
  }, [loadCurrentImage]);

  const getStatusLabel = (status?: string) => {
    if (!status) return 'N/A';
    const normalized = status.toLowerCase();
    if (normalized === 'pending_cancel') return 'Pending Cancel';
    if (normalized === 'pending_activation') return 'Pending Activation';
    if (normalized === 'cancelled' || normalized === 'canceled') return 'Canceled';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusTone = (status?: string): AdminStatusPillTone => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'active') return 'success';
    if (normalized === 'pending_activation' || normalized === 'trial' || normalized === 'pending_cancel') return 'warning';
    if (normalized === 'canceled' || normalized === 'cancelled') return 'danger';
    return 'neutral';
  };

  const handleCopyStoreKey = async () => {
    const storeKey = store?.store_key?.trim();
    if (!storeKey) return;

    try {
      await navigator.clipboard.writeText(storeKey);
      toast.success('Store key copied');
    } catch (error) {
      toast.error('Failed to copy store key');
    }
  };

  const handleStoreMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setStoreMenuAnchor(event.currentTarget);
  }, []);

  const handleStoreMenuClose = useCallback(() => {
    setStoreMenuAnchor(null);
  }, []);

  const handleStoreMenuItemClick = useCallback(
    (nextStoreId: string) => {
      if (!nextStoreId || nextStoreId === (activeStoreId || store?.id)) {
        handleStoreMenuClose();
        return;
      }
      onStoreChange?.(nextStoreId);
      handleStoreMenuClose();
    },
    [activeStoreId, handleStoreMenuClose, onStoreChange, store?.id],
  );

  const activeSwitcherStoreId = activeStoreId || store?.id || '';
  const showStoreMenu = enableStoreSwitcher && storeSwitcherOptions.length > 1;

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ pb: 1.25 }}
      >
        <Breadcrumbs>
          <Button onClick={onBack} variant="text" sx={adminLinkButtonSx}>
            {backLabel}
          </Button>
          <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
            Store Details
          </Typography>
        </Breadcrumbs>
        {globalActions ? <Box sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' } }}>{globalActions}</Box> : null}
      </Stack>

      <AdminSurface sx={{ p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <Box sx={{ flex: 1.5, minWidth: 0 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                component="img"
                src={previewUrl}
                alt="store logo"
                sx={{ borderRadius: adminRadii.sm, height: 48, objectFit: 'contain', width: 48 }}
              />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box
                  component="h1"
                  sx={{
                    ...adminTypography.sectionTitle,
                    alignItems: 'center',
                    color: adminColors.text,
                    display: 'flex',
                    gap: 0.75,
                    m: 0,
                    minWidth: 0,
                  }}
                >
                  <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {store?.name || 'Loading store...'}
                  </Box>
                  {showStoreMenu ? (
                    <>
                      <IconButton
                        aria-label="Switch store"
                        aria-controls={storeMenuAnchor ? 'store-switcher-menu' : undefined}
                        aria-expanded={storeMenuAnchor ? 'true' : undefined}
                        aria-haspopup="menu"
                        size="small"
                        onClick={handleStoreMenuOpen}
                        sx={adminIconActionButtonSx}
                      >
                        <KeyboardArrowDownRoundedIcon fontSize="inherit" />
                      </IconButton>
                      <Menu
                        id="store-switcher-menu"
                        anchorEl={storeMenuAnchor}
                        open={Boolean(storeMenuAnchor)}
                        onClose={handleStoreMenuClose}
                        MenuListProps={{ 'aria-label': 'Switch store' }}
                      >
                        {storeSwitcherOptions.map((candidate) => (
                          <MenuItem
                            key={candidate.id}
                            selected={candidate.id === activeSwitcherStoreId}
                            onClick={() => handleStoreMenuItemClick(candidate.id)}
                          >
                            {candidate.name || candidate.store_key || candidate.id}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : null}
                  {showEdit && onEdit ? (
                    <IconButton aria-label="Edit store" size="small" onClick={onEdit} sx={adminIconActionButtonSx}>
                      <EditRoundedIcon fontSize="inherit" />
                    </IconButton>
                  ) : null}
                </Box>
                <Box sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText, display: 'inline', fontSize: '14px', mt: 0.25 }}>
                  {store?.address_line1 || store?.address || '—'},
                </Typography>
                <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText, display: 'inline', fontSize: '14px', ml: 0.5 }}>
                  {[store?.address_line2, store?.city, store?.state, store?.postal_code || store?.zip_code, store?.country]
                    .filter(Boolean)
                    .join(' ')}
                </Typography>
                </Box>
                <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                  <Box sx={{ alignItems: 'center', display: 'flex', }}>
                    <Typography sx={{ ...adminTypography.body, color: adminColors.textSoft }}>
                    <Box component="span" sx={{ color: adminColors.subtleText, fontWeight: 500 }}>
                      Store Key:
                    </Box>{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: '#222B45' }}>
                      {store?.store_key || '—'}
                    </Box>
                  </Typography>
                  <IconButton
                    aria-label="Copy store key"
                    size="small"
                    disabled={!store?.store_key}
                    onClick={handleCopyStoreKey}
                    sx={adminIconActionButtonSx}
                  >
                    <ContentCopyRoundedIcon fontSize="inherit" />
                  </IconButton>
                  </Box>
                {store?.store_closing_time ? (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ alignSelf: 'center', borderColor: adminColors.border, flexShrink: 0, height: 18 }}
                  />
                ) : null}
                {store?.store_closing_time ? (
                  <Typography sx={{ ...adminTypography.body, color: adminColors.textSoft }}>
                    Closing Time: <Box component="span" sx={{ fontWeight: 600, color: '#222B45', }}>{storeClosingTime}</Box>
                  </Typography>
                ) : null}
                </Box>
              </Box>
            </Stack>
          </Box>

          <Divider orientation="vertical" flexItem sx={adminCombineSx(adminDividerSx, { display: { xs: 'none', md: 'block' } })} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.primary, mb: 0.75 }}>
              Store Owner Contact
            </Typography>
            <Box display="flex" alignItems="center" gap={1.25}>
              <Avatar
                sx={{
                  backgroundColor: adminColors.primarySoft,
                  borderRadius: adminRadii.sm,
                  color: adminColors.text,
                  fontSize: '16px',
                  fontWeight: 600,
                  height: 44,
                  width: 44,
                }}
              >
                {storeOwner?.display_name?.charAt(0) || '—'}
              </Avatar>
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px', }}>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                  {storeOwner?.display_name || '—'}
                </Typography>
                <Typography sx={{ ...adminTypography.body, color: adminColors.subtleText, overflowWrap: 'anywhere' }}>
                  {storeOwner?.contact_email || '—'}
                </Typography>
                <Typography sx={{ ...adminTypography.body, color: adminColors.subtleText }}>
                  {formatPhoneNumber(storeOwner?.phone_number) || '—'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={adminCombineSx(adminDividerSx, { display: { xs: 'none', md: 'block' } })} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.primary, mb: 0.75 }}>
              Subscription Details
            </Typography>
            <Typography sx={{ ...adminTypography.body, color: adminColors.subtleText }}>
              Start Date:{' '}
              <Box component="span" sx={{ color: adminColors.text, fontWeight: 600 }}>
                {formatDateMMDDYYYY(store?.subscription_start_date, '—')}
              </Box>
            </Typography>
            <Typography sx={{ ...adminTypography.body, color: adminColors.subtleText }}>
              Renewal Date:{' '}
              <Box component="span" sx={{ color: adminColors.text, fontWeight: 600 }}>
                {formatDateMMDDYYYY(store?.renewal_date, '—')}
              </Box>
            </Typography>
            <Typography sx={{ ...adminTypography.body, color: adminColors.subtleText, mt:0.5, }}>
              Status:{' '}
              <Box component="span" sx={{ color: adminColors.text, fontWeight: 600 }}>
                {store?.subscription_status ? (
                <AdminStatusPill
                  label={getStatusLabel(store.subscription_status)}
                  tone={getStatusTone(store.subscription_status)}
                />
              ) : (
                <Box component="span" sx={{ ...adminTypography.bodyStrong, color: adminColors.subtleText }}>—</Box>
              )}
              </Box>
            </Typography>
            {/* <Box sx={{ mt: 1 }}>
              {store?.subscription_status ? (
                <AdminStatusPill
                  label={getStatusLabel(store.subscription_status)}
                  tone={getStatusTone(store.subscription_status)}
                />
              ) : (
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.subtleText }}>—</Typography>
              )}
            </Box> */}
          </Box>
        </Stack>
      </AdminSurface>

      {(tabs.length > 0 || rightActions) && (
        <AdminSurface
          sx={{
            backgroundColor: 'rgb(239, 246, 255)', 
            border: '1px solid rgba(163, 174, 208, 0.24)',
            borderBottom: 'none',
            borderRadius: '8px 8px 0 0',
            mb: 0,
            pl: '12px',
            pr: 1.5,
            py: 0,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            {tabs.length > 0 ? (
              <Tabs
                value={activeTab ?? false}
                onChange={(_event, nextTab: string) => onTabChange?.(nextTab)}
                variant="scrollable"
                scrollButtons="auto"
                sx={adminCombineSx(adminTabsSx, { flex: 1, minWidth: 0 })}
              >
                {tabs.map((tab) => (
                  <Tab key={tab.key} label={tab.label} value={tab.key} />
                ))}
              </Tabs>
            ) : null}
            {rightActions ? <Box sx={{ flexShrink: 0 }}>{rightActions}</Box> : null}
          </Stack>
        </AdminSurface>
      )}
    </>
  );
};

export default React.memo(StoreDetailsHeader);
